import { useEffect, useMemo } from 'react';
import { Head, HeadLine, HeadWrapper, Title, Wrapper } from './catalog-page.styles';
import { SegmantTabs } from '@shared/ui/segment-tabs/segment-tabs';
import { Button } from '@vtbhome/juicyfront';
import { ArrowsChevronLeft } from '@vtbhome/juicyfront/indexIcon';
import { useHistory } from 'react-router-dom';
import { tabs } from '../utils/tabs/tabs';
import { useProductsListSharedQuery } from '@entities/product/libs/hooks/use-products-list-shared-query/use-products-list-shared-query';
import { ListProducts } from '@widgets/list-products/ui/list-products';
import { TabValues } from '../config/ui-constants';
import {
    DEFAULT_PRODUCTS_LIST_PARAMETERS,
    DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS,
    DEFAULT_USER_ON_REVIEW_PRODUCTS_LIST_PARAMETERS,
} from '@entities/product/config/store-constants';
import { useUserProductsListSharedQuery } from '@entities/product/libs/hooks/use-user-products-list-shared-query/use-user-products-list-shared-query';
import { ProductsListDataRequest, UserProductsListDataRequest } from '@entities/product/api/product-types';
import { ListUserProducts } from '@widgets/list-user-products/ui/list-user-products';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { SortListProducts } from '@features/product/sort-list-user-products/ui/sort-list-user-products';
import { FilterCatalogProducts } from '@features/product/filter-catalog-products/ui/filter-catalog-products';
import { useUrlTabs } from '@shared/lib/hooks/use-url-tabs/use-url-tabs';
import { UserRoles } from '@shared/model/user';
import { useSearchParams } from '@shared/lib/hooks/use-search-params/use-search-params';
import { sortParametersAfterClearSearch } from '../utils/sort-parameters-after-clear-search/sort-parameters-after-clear-search';

export const CatalogPage = () => {
    const history = useHistory();
    const user = useCurrentUserSelector();

    const [productsListParameters, setProductsListParameters] = useSearchParams<ProductsListDataRequest>(
        DEFAULT_PRODUCTS_LIST_PARAMETERS
    );

    const [userProductsOnReviewListParameters, setUserProductsOnReviewListParameters] =
        useSearchParams<UserProductsListDataRequest>({
            ...DEFAULT_USER_ON_REVIEW_PRODUCTS_LIST_PARAMETERS,
            userId: user?.id || 0,
        });
    const [userCompletedProductsParameters, setUserCompletedProductsParameters] =
        useSearchParams<UserProductsListDataRequest>({
            ...DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS,
            userId: user?.id || 0,
        });

    const [getProducts, getProductsResult] = useProductsListSharedQuery(productsListParameters);
    const [getUserCompletedProducts, getUserCompletedProductsResult] = useUserProductsListSharedQuery(
        userCompletedProductsParameters
    );
    const [getUserOnReviewProducts, getUserOnReviewProductsResult] = useUserProductsListSharedQuery(
        userProductsOnReviewListParameters
    );
    const userRole = useMemo(() => user?.role.code, [user]);
    const listTabs = useMemo(() => {
        return tabs(
            {
                catalogCount: getProductsResult.data?.total,
                completedCount: getUserCompletedProductsResult.data?.total,
                reviewCount: getUserOnReviewProductsResult.data?.total,
            },
            userRole
        );
    }, [
        getProductsResult.data?.total,
        getUserCompletedProductsResult.data?.total,
        getUserOnReviewProductsResult.data?.total,
        userRole,
    ]);

    const { tabValue, handleSetTabValue } = useUrlTabs({
        tabValues: Object.values(TabValues),
        initialTabValue: TabValues.Catalog,
    });

    const parametersByActiveTab = useMemo(() => {
        switch (tabValue) {
            case TabValues.Catalog: {
                return {
                    parameters: productsListParameters,
                    setParameters: setProductsListParameters,
                };
            }
            case TabValues.Completed: {
                return {
                    parameters: userCompletedProductsParameters,
                    setParameters: setUserCompletedProductsParameters,
                };
            }
            case TabValues.Review: {
                return {
                    parameters: userProductsOnReviewListParameters,
                    setParameters: setUserProductsOnReviewListParameters,
                };
            }
            default: {
                return {
                    parameters: undefined,
                    setParameters: undefined,
                };
            }
        }
    }, [tabValue, productsListParameters, userCompletedProductsParameters, userProductsOnReviewListParameters]);

    const handleChangeActivetab = (tabValue: string) => {
        setProductsListParameters({ ...productsListParameters });
        handleSetTabValue(tabValue);
    };

    useEffect(() => {
        getUserCompletedProducts(userCompletedProductsParameters);
        getUserOnReviewProducts(userProductsOnReviewListParameters);
        getProducts(productsListParameters);
    }, [userCompletedProductsParameters, userProductsOnReviewListParameters, productsListParameters]);

    return (
        <Wrapper>
            <HeadWrapper>
                <Head>
                    <HeadLine>
                        {Boolean(history.length) && (
                            <Button
                                round
                                size='xl'
                                buttonType='secondary'
                                startAdornment={<ArrowsChevronLeft />}
                                onClick={() => history.goBack()}
                            />
                        )}
                        <Title size='h6'>Список продуктов и мероприятий</Title>
                        <FilterCatalogProducts
                            sortParametersAfterClearSearch={sortParametersAfterClearSearch(tabValue)}
                            productsListParameters={parametersByActiveTab.parameters}
                            setProductsListParameters={parametersByActiveTab.setParameters}
                        />
                    </HeadLine>
                    <HeadLine>
                        <SegmantTabs list={listTabs} activeTabValue={tabValue} onChange={handleChangeActivetab} />
                        <SortListProducts
                            parameters={parametersByActiveTab.parameters}
                            setParameters={parametersByActiveTab.setParameters}
                        />
                    </HeadLine>
                </Head>
            </HeadWrapper>
            {tabValue === TabValues.Catalog && (
                <ListProducts
                    productsListParameters={productsListParameters}
                    setProductsListParameters={setProductsListParameters}
                />
            )}
            {((userRole !== UserRoles.Employee && tabValue === TabValues.Review) ||
                tabValue === TabValues.Completed) && (
                <ListUserProducts
                    userProductsListParameters={
                        tabValue === TabValues.Review
                            ? userProductsOnReviewListParameters
                            : userCompletedProductsParameters
                    }
                    setUserProductsListParameters={
                        tabValue === TabValues.Review
                            ? setUserProductsOnReviewListParameters
                            : setUserCompletedProductsParameters
                    }
                />
            )}
        </Wrapper>
    );
};
